// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";

import { ReactComponent as HomeSmile } from "../assets/icons/home-smile.svg";
import { ReactComponent as SearchIcon } from "../assets/icons/search-sm.svg";
import { ReactComponent as MarkerPin } from "../assets/icons/marker-pin-03.svg";
import { ReactComponent as WindIcon } from "../assets/icons/wind-02.svg";
import { ReactComponent as SunIcon } from "../assets/icons/sun.svg";
import { ReactComponent as RainIcon } from "../assets/icons/cloud-raining-05.svg";

import AssetImage from "./AssetImage";
import OrganizationPreview from "./Organization/OrganizationPreview";
import { setFlyLocation } from "../store/mapStateSlice";
import { imageCache } from "../utils/imageCache";
import bgpattern from "../assets/images/bg-pattern-01.svg";
import { Skeleton } from "./Skeleton";

// The Asset page's two group-page-derived parts: the peek rail listing the group's other assets (its asset card compressed to a row), and the masthead block carrying this asset's portrait, name and details (its group masthead, asset-side).

const formatCoordinate = (value, digits = 5) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : "—";

// --------------------------------------------------------------------------- Left rail — the group's assets, so you can move between them without going back ---------------------------------------------------------------------------

const AssetSwitcherRow = ({ asset, groupId, isActive, onNavigate }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleClick = () => {
    navigate(`/home/group/${groupId}/asset/${asset.id}`);
    dispatch(setFlyLocation([asset.lng, asset.lat]));
    onNavigate?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-current={isActive ? "page" : undefined}
      data-testid="asset-switcher-row"
      className={`flex w-full items-start gap-2.5 rounded-eai-card px-2.5 py-2 text-left transition-colors ${
        isActive
          ? "bg-eai-accent-lt hover:bg-eai-accent-lt"
          : "hover:bg-eai-surface active:bg-eai-surface-active"
      }`}
    >
      <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-eai-card border border-eai-border bg-eai-surface">
        <AssetImage
          asset={asset}
          assetName={asset?.name || "Asset"}
          size={40}
          className="block rounded-none border-0"
          fallbackIcon={<HomeSmile className="h-5 w-5" />}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-outfit text-sm text-eai-ink">{asset.name}</span>
        <span className="mt-0.5 flex items-center gap-1 font-inter text-xs text-eai-ink-subtle">
          <MarkerPin className="h-3 w-3 shrink-0 [&_path]:stroke-current" />
          <span className="truncate">
            {formatCoordinate(asset.lat, 3)}, {formatCoordinate(asset.lng, 3)}
          </span>
        </span>
      </span>
    </button>
  );
};

export const AssetSwitcherPanel = ({ assetList = [], groupId, activeAssetId, onNavigate }) => {
  const [searchValue, setSearchValue] = useState("");

  const filtered = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) return assetList;
    return assetList.filter((asset) =>
      [asset?.name, asset?.description, ...(asset?.tags || [])].some((field) =>
        (field || "").toLowerCase().includes(term)
      )
    );
  }, [assetList, searchValue]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-none px-3 pb-2 pt-3">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-eai-ink-faint [&_path]:stroke-current" />
          <input
            type="text"
            className="eai-input pl-9"
            placeholder="Search assets…"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            aria-label="Search assets in this group"
          />
        </div>
      </div>

      <div className="flex flex-none items-center gap-2.5 px-3 pb-1">
        <span className="eai-eyebrow">
          {searchValue.trim() ? `${filtered.length} of ${assetList.length}` : `${assetList.length} Assets`}
        </span>
        <span className="h-px flex-1 bg-eai-border" />
      </div>

      <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 pb-3 pt-1">
        {filtered.length === 0 ? (
          <p className="eai-description-sm px-2 py-6 text-center">No assets match that search.</p>
        ) : (
          filtered.map((asset) => (
            <AssetSwitcherRow
              key={asset.id}
              asset={asset}
              groupId={groupId}
              isActive={String(asset.id) === String(activeAssetId)}
              onNavigate={onNavigate}
            />
          ))
        )}
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------- Masthead — the asset's identity block, on the group page's masthead shape ---------------------------------------------------------------------------

// The energy models an asset can carry, keyed off the same curve fields the group page filters on
const ENERGY_MODEL_BADGES = [
  { field: "windCurveId", label: "Wind", Icon: WindIcon },
  { field: "solarCurveId", label: "Solar", Icon: SunIcon },
  { field: "precipCurveId", label: "Precipitation", Icon: RainIcon },
];

// One fact about the asset. Deliberately NOT a pill: `rounded-full border bg-eai-surface-control` is character-for-character `.eai-chip`, the DS's *interactive* chip, so a read-only fact wearing it reads as a button. Flat label/value is unmistakably data.
const DetailFact = ({ label, children }) => (
  <span className="inline-flex items-baseline gap-1.5">
    <span className="eai-meta-label">{label}</span>
    <span className="font-outfit text-eai-control text-eai-ink">{children}</span>
  </span>
);

export const AssetMasthead = ({
  asset,
  assetGroup,
  organization,
  hasProcessedModels,
  onEdit,
  onOpenResults,
}) => {
  const [portraitUrl, setPortraitUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setPortraitUrl(null);
    if (!asset?.imageUrl) return undefined;

    imageCache
      .getSignedUrl(asset.imageUrl)
      .then((url) => {
        if (!cancelled) setPortraitUrl(url || null);
      })
      .catch((error) => {
        console.error(`AssetMasthead: could not load image for ${asset?.name}:`, error);
      });

    return () => {
      cancelled = true;
    };
  }, [asset?.imageUrl, asset?.name]);

  const energyModels = ENERGY_MODEL_BADGES.filter(({ field }) => asset?.[field]);
  const tags = (asset?.tags || []).filter(Boolean);
  const assetValue =
    asset?.value?.value != null ? `${asset.value.value} ${asset.value.currency || ""}`.trim() : null;
  const hasIdentity = !!organization || tags.length > 0;

  const facts = [
    assetValue && { label: "Value", value: assetValue },
    asset?.client && { label: "Client", value: asset.client },
  ].filter(Boolean);

  return (
    <div className="eai-gap-header">
      <div className="flex flex-col gap-3 sm:gap-5">
        <div className="flex items-start gap-3 sm:items-center sm:gap-5">
          {/* Portrait on the card radius and lift, matching the group page's image. It is also the page's edit affordance — hover: is a no-op on touch, so active: carries it there. Without a picture it is the pattern, badged so the block still reads as an asset. */}
          <div
            role="button"
            tabIndex={0}
            title="Edit asset"
            aria-label="Edit asset"
            onClick={onEdit}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onEdit?.();
              }
            }}
            className="eai-media h-16 w-16 shrink-0 cursor-pointer transition-opacity hover:opacity-90 active:opacity-75 sm:h-28 sm:w-28"
            style={{ backgroundImage: `url(${portraitUrl || bgpattern})` }}
          >
            {!portraitUrl && (
              <div className="flex h-full w-full items-center justify-center">
                <HomeSmile className="h-7 w-7 sm:h-9 sm:w-9" />
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {/* The eyebrow names the asset's group and is the way back to it — the group page makes the same move up to all groups */}
              {assetGroup?.id ? (
                <Link
                  to={`/home/group/${assetGroup.id}`}
                  title="Back to group"
                  className="group inline-flex items-center"
                >
                  <span className="eai-slash mr-1.5 text-sm leading-none">{"//"}</span>
                  <span className="eai-eyebrow transition-colors group-hover:text-eai-ink group-active:text-eai-ink-soft">
                    {assetGroup.name || organization?.name || "Asset Group"}
                  </span>
                </Link>
              ) : (
                <span className="inline-flex items-center">
                  <span className="eai-slash mr-1.5 text-sm leading-none">{"//"}</span>
                  <span className="eai-eyebrow">{organization?.name || "Asset"}</span>
                </span>
              )}
              {energyModels.map(({ field, label, Icon }) => (
                <span
                  key={field}
                  className="eai-tag-pill-sm inline-flex items-center gap-1 border-eai-cta-primary bg-eai-cta-primary text-white"
                >
                  <Icon className="h-3 w-3 [&_path]:stroke-current" />
                  {label}
                </span>
              ))}
            </div>

            {/* The asset's name is the page's h1 — the group page made the same move */}
            <h1 className="eai-page-title mt-1 break-words">{asset?.name}</h1>

            {/* Clamped — unbounded, a long description alone fills a phone screen and pushes the facts and chart below the fold */}
            {asset?.description && (
              <p className="eai-description-sm mt-1.5 line-clamp-3 break-words sm:line-clamp-2">
                {asset.description}
              </p>
            )}

            {/* Read-only specifications, sitting with the copy they describe. No dividers: the uppercase label already delimits each pair, and a divider strands itself at the end of a wrapped line. Lat/lng are absent by design — the map carries the location. */}
            {facts.length > 0 && (
              <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 sm:mt-2.5 sm:gap-x-5">
                {facts.map(({ label, value }) => (
                  <DetailFact key={label} label={label}>
                    {value}
                  </DetailFact>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Organization and tags — these stay pills because an org chip and a tag legitimately are ones. Organization leads on the left and the page action sits alone on the right, the same shape as the group page. Column below sm so the group hugs the left edge whether or not there is an organization. */}
        {(hasIdentity || hasProcessedModels) && (
          <div className="flex flex-wrap items-center gap-3">
            {hasIdentity && (
              <div className="mr-auto flex min-w-0 flex-wrap items-center gap-1.5 sm:gap-2">
                {organization ? <OrganizationPreview organization={organization} /> : null}
                {tags.map((tag) => (
                  <span key={tag} className="eai-tag-pill-sm">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {hasProcessedModels && (
              <button
                type="button"
                onClick={onOpenResults}
                data-tour="asset-results"
                className="eai-btn-cta"
              >
                Results
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

// Placeholder matching AssetMasthead's layout — keep the two in sync when the masthead changes.
export const AssetMastheadSkeleton = () => (
  <div className="eai-gap-header">
    <div className="flex flex-col gap-3 sm:gap-5">
      <div className="flex items-start gap-3 sm:items-center sm:gap-5">
        <Skeleton className="h-16 w-16 shrink-0 rounded-eai-card sm:h-28 sm:w-28" radius="none" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-5 w-20" radius="full" />
          </div>
          <Skeleton className="mt-2 h-7 w-2/3 max-w-sm" />
          <Skeleton className="mt-1.5 h-3 w-1/2 max-w-xs" />
          {/* The read-only facts row */}
          <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-3 w-20" />
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Skeleton className="mr-auto h-6 w-32" radius="full" />
        <Skeleton className="h-eai-control-lg w-24" radius="md" />
      </div>
    </div>
  </div>
);
